import React from 'react';
import Icon from '../../../components/AppIcon';

const ContentValidation = ({ artistData }) => {
  const validationChecks = [
    {
      section: 'Profile',
      checks: [
        { name: 'Artist Name', status: artistData?.profile?.name ? 'valid' : 'missing', required: true },
        { name: 'Email', status: artistData?.profile?.email ? 'valid' : 'missing', required: true },
        { name: 'Phone', status: artistData?.profile?.phone ? 'valid' : 'missing', required: false },
        { name: 'Website', status: artistData?.profile?.website ? 'valid' : 'missing', required: false }
      ]
    },
    {
      section: 'Promotion',
      checks: [
        { name: 'Splash Image', status: artistData?.promotion?.splashImage ? 'valid' : 'missing', required: true },
        { name: 'Logo', status: artistData?.promotion?.logo ? 'valid' : 'missing', required: true },
        { name: 'Home Image', status: artistData?.promotion?.homeImage ? 'valid' : 'missing', required: false }
      ]
    },
    {
      section: 'About',
      checks: [
        { name: 'Description', status: artistData?.about?.description ? 'valid' : 'missing', required: true },
        { name: 'Header Image', status: artistData?.about?.headerImage ? 'valid' : 'missing', required: false }
      ]
    },
    {
      section: 'Calendar',
      checks: [
        { name: 'Events', status: artistData?.calendar?.length > 0 ? 'valid' : 'missing', required: false }
      ]
    },
    {
      section: 'Work',
      checks: [
        { name: 'Portfolio Items', status: artistData?.work?.length > 0 ? 'valid' : 'missing', required: true }
      ]
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <Icon name="CheckCircle" size={16} className="text-green-600" />;
      case 'missing':
        return <Icon name="AlertCircle" size={16} className="text-red-600" />;
      case 'warning':
        return <Icon name="AlertTriangle" size={16} className="text-yellow-600" />;
      default:
        return <Icon name="Circle" size={16} className="text-gray-400" />;
    }
  };

  const getStatusColor = (status, required) => {
    if (status === 'valid') return 'text-green-600';
    if (status === 'missing' && required) return 'text-red-600';
    if (status === 'missing' && !required) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const calculateCompleteness = () => {
    const allChecks = validationChecks?.flatMap(section => section?.checks);
    const validChecks = allChecks?.filter(check => check?.status === 'valid');
    return Math.round((validChecks?.length / allChecks?.length) * 100);
  };

  const completeness = calculateCompleteness();

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Content Validation</h3>
        <div className="flex items-center space-x-2">
          <div className="w-12 h-12 relative">
            <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="2"
              />
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke={completeness >= 80 ? "#10b981" : completeness >= 60 ? "#f59e0b" : "#ef4444"}
                strokeWidth="2"
                strokeDasharray={`${completeness}, 100`}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-semibold text-gray-900">{completeness}%</span>
            </div>
          </div>
        </div>
      </div>
      {/* Overall Status */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          {completeness >= 80 ? (
            <Icon name="CheckCircle" size={20} className="text-green-600" />
          ) : completeness >= 60 ? (
            <Icon name="AlertTriangle" size={20} className="text-yellow-600" />
          ) : (
            <Icon name="AlertCircle" size={20} className="text-red-600" />
          )}
          <div>
            <p className="font-medium text-gray-900">
              {completeness >= 80 ? 'Ready for Mobile App' : 
               completeness >= 60 ? 'Needs Minor Updates': 'Requires Attention'}
            </p>
            <p className="text-sm text-gray-600">
              {completeness >= 80 ? 'All essential content is complete' : 
               completeness >= 60 ? 'Some optional content is missing': 'Missing required content for mobile app'}
            </p>
          </div>
        </div>
      </div>
      {/* Section Validation */}
      <div className="space-y-4">
        {validationChecks?.map((section) => (
          <div key={section?.section}>
            <h4 className="text-sm font-medium text-gray-700 mb-2">{section?.section}</h4>
            <div className="space-y-2">
              {section?.checks?.map((check, index) => (
                <div key={index} className="flex items-center justify-between py-1">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(check?.status)}
                    <span className="text-sm text-gray-900">{check?.name}</span>
                    {check?.required && (
                      <span className="text-xs text-red-500">*</span>
                    )}
                  </div>
                  <span className={`text-xs font-medium ${getStatusColor(check?.status, check?.required)}`}>
                    {check?.status === 'valid' ? 'Complete' : 
                     check?.required ? 'Required' : 'Optional'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Mobile Optimization */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Mobile Optimization</h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-green-600" />
              <span className="text-sm text-gray-900">Image Dimensions</span>
            </div>
            <span className="text-xs text-green-600 font-medium">Optimized</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-green-600" />
              <span className="text-sm text-gray-900">Text Readability</span>
            </div>
            <span className="text-xs text-green-600 font-medium">Good</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-yellow-600" />
              <span className="text-sm text-gray-900">Load Performance</span>
            </div>
            <span className="text-xs text-yellow-600 font-medium">Fair</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentValidation;